// ── DESIGNER PROFILE: shape, limits and validation ───────────────────────────
// Single source of truth for the designer profile. The registration step, the
// Settings editor and (later) the search filters all read from here, so a limit
// can never drift between where it's enforced and where it's displayed.

// THREE ROLES, ONE FIELD on users/{uid}. A `shop` supplies materials,
// furniture and fittings, and behaves like a designer in every way that
// concerns the rest of the app — a public profile, published photos that can
// be liked, viewed, commented on and shared, a place in search and in the feed
// — while describing itself with a different set of fields. That shape lives
// in constants/shop.js; only the role name belongs here, beside the others, so
// there is one list of what a user can be.
export const USER_TYPES = { CUSTOMER: 'customer', DESIGNER: 'designer', SHOP: 'shop' };

// Stable machine keys — these are what get written to Firestore and queried
// against. The human label comes from i18n (`ds_style_<key>`), so a style can
// be renamed or translated without migrating a single document.
export const DESIGNER_STYLES = [
  'modern', 'minimalist', 'scandinavian', 'industrial', 'loft',
  'classic', 'neoclassic', 'contemporary', 'art_deco', 'mid_century',
  'bohemian', 'rustic', 'farmhouse', 'provence', 'mediterranean',
  'japandi', 'high_tech', 'eclectic',
];

export const LIMITS = {
  fullName:    { min: 2,  max: 80  },
  // 250 keeps the bio short enough to render IN FULL on the profile header —
  // the old 600 forced a two-line clamp there, which hid most of what was
  // written. A limit you can display beats a longer one you have to truncate.
  description: {          max: 250 },
  websites:    { max: 3 },
  contacts:    { max: 5 },
  contactValue:{ max: 120 },
  experience:  { min: 0, max: 70 },
  hourlyRate:  { min: 0, max: 100000 },
  styles:      { max: 8 },            // keeps the profile card readable
};

// The currencies designers here actually bill in.
//
// This was a free-text box, which meant the same currency arrived as "amd",
// "AMD", "Dram" and "֏" — impossible to sort, filter or compare a rate across.
// A short fixed list is worth more than an open field: the code is what gets
// stored, the symbol is only for display.
export const CURRENCIES = [
  { code: 'AMD', symbol: '֏' },
  { code: 'USD', symbol: '$' },
  { code: 'EUR', symbol: '€' },
  { code: 'RUB', symbol: '₽' },
];

export const CURRENCY_CODES = CURRENCIES.map(c => c.code);
export const currencySymbolOf = (code) =>
  (CURRENCIES.find(c => c.code === code) || CURRENCIES[0]).symbol;

// What the rate is charged PER. Previously everything was assumed hourly, which
// was simply wrong for anyone quoting by area — the commonest way to price an
// interior job here.
export const RATE_UNITS = [
  { id: 'hour',    perLabel: 'hour' },
  { id: 'sqm',     perLabel: 'm²' },
  { id: 'project', perLabel: 'project' },
  { id: 'day',     perLabel: 'day' },
];

export const RATE_UNIT_IDS = RATE_UNITS.map(u => u.id);

// Avatar is stored INLINE on users/{uid} as a compressed data URL rather than
// in Cloud Storage. At 256px / JPEG q0.82 an avatar lands around 15-25KB —
// far inside Firestore's 1MB document ceiling — which means profile pictures
// work today without Storage being provisioned. Gallery photos are a different
// matter (many, larger, and they need CDN delivery); those get real Storage.
export const AVATAR_MAX_PX    = 256;
export const AVATAR_QUALITY   = 0.82;
export const AVATAR_MAX_BYTES = 120 * 1024;   // hard stop; ~5x typical output

const URL_RE = /^https?:\/\/[^\s.]+\.[^\s]{2,}$/i;
// Permissive by design: international numbers vary wildly and a strict pattern
// rejects more real numbers than it catches fake ones.
const PHONE_RE = /^[+]?[\d][\d\s\-().]{5,}$/;

export function isValidWebsite(v) {
  const s = String(v || '').trim();
  return !s || URL_RE.test(s);
}

// A contact row is a link OR a phone number — the designer picks per row.
export function isValidContact(v) {
  const s = String(v || '').trim();
  return !s || URL_RE.test(s) || PHONE_RE.test(s);
}

// Normalises a bare domain to a URL so "studio.com" saves as a working link
// instead of a relative path that 404s when clicked.
export function normalizeUrl(v) {
  const s = String(v || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

// Validates the whole designer profile. Returns a { field: messageKey } map so
// callers can render errors wherever they like — the keys are i18n keys, not
// English text, since this runs behind three locales.
export function validateDesignerProfile(p) {
  const errors = {};
  const name = String(p.fullName || '').trim();
  if (name.length < LIMITS.fullName.min) errors.fullName = 'ds_err_name';
  else if (name.length > LIMITS.fullName.max) errors.fullName = 'ds_err_name_long';

  if (String(p.description || '').length > LIMITS.description.max) {
    errors.description = 'ds_err_desc_long';
  }

  if (p.experienceYears !== '' && p.experienceYears != null) {
    const n = Number(p.experienceYears);
    if (!Number.isInteger(n) || n < LIMITS.experience.min || n > LIMITS.experience.max) {
      errors.experienceYears = 'ds_err_experience';
    }
  }

  if (p.hourlyRate !== '' && p.hourlyRate != null) {
    const r = Number(p.hourlyRate);
    if (!Number.isFinite(r) || r < LIMITS.hourlyRate.min || r > LIMITS.hourlyRate.max) {
      errors.hourlyRate = 'ds_err_rate';
    }
  }

  if ((p.styles || []).length > LIMITS.styles.max) errors.styles = 'ds_err_styles_max';

  const sites = (p.websites || []).filter(Boolean);
  if (sites.length > LIMITS.websites.max) errors.websites = 'ds_err_sites_max';
  else if (sites.some(s => !isValidWebsite(s))) errors.websites = 'ds_err_site_invalid';

  const contacts = (p.contacts || []).filter(c => c && String(c.value || '').trim());
  if (contacts.length > LIMITS.contacts.max) errors.contacts = 'ds_err_contacts_max';
  else if (contacts.some(c => !isValidContact(c.value))) errors.contacts = 'ds_err_contact_invalid';

  return errors;
}

// Strips a raw form state down to exactly what gets persisted: trimmed, capped,
// blanks dropped. Kept separate from validation so the write path can't
// accidentally store an over-long or half-empty row that validation missed.
export function serializeDesignerProfile(p) {
  return {
    fullName: String(p.fullName || '').trim().slice(0, LIMITS.fullName.max),
    styles: Array.isArray(p.styles)
      ? p.styles.filter(s => DESIGNER_STYLES.includes(s)).slice(0, LIMITS.styles.max)
      : [],
    experienceYears:
      p.experienceYears === '' || p.experienceYears == null
        ? null
        : Math.max(LIMITS.experience.min, Math.min(LIMITS.experience.max, Number(p.experienceYears) | 0)),
    hourlyRate:
      p.hourlyRate === '' || p.hourlyRate == null
        ? null
        : Math.max(LIMITS.hourlyRate.min, Math.min(LIMITS.hourlyRate.max, Math.round(Number(p.hourlyRate)))),
    // A fixed enum now, not free text. Anything unrecognised — including the
    // codes typed by hand before this list existed — falls back to the first
    // currency rather than being stored as a value nothing can interpret.
    currency: CURRENCY_CODES.includes(p.currency) ? p.currency : CURRENCY_CODES[0],
    // What the rate is charged per. Profiles saved before this field existed
    // have no value, and hourly is what they meant.
    rateUnit: RATE_UNIT_IDS.includes(p.rateUnit) ? p.rateUnit : RATE_UNIT_IDS[0],
    location: String(p.location || '').trim().slice(0, 120),
    // THE FULL LINE THE USER PICKED — "45, Khachatur Abovyan street, Arinj,
    // Armenia" — as opposed to `location`, which is trimmed to "place,
    // country" because search GROUPS on it and would otherwise show one filter
    // entry per profile. This is what the profile displays. Longer cap: a real
    // street address does not fit in 120 characters once it carries a district
    // and a postcode.
    locationAddress: String(p.locationAddress || p.location || '').trim().slice(0, 250),
    // Kept alongside the display string: the string is what search groups on,
    // the coordinates leave 'designers near me' open without a migration.
    locationCoords: (p.locationCoords && Number.isFinite(Number(p.locationCoords.lat)))
      ? { lat: Number(p.locationCoords.lat), lng: Number(p.locationCoords.lng) }
      : null,
    description: String(p.description || '').trim().slice(0, LIMITS.description.max),
    websites: (p.websites || [])
      .map(normalizeUrl).filter(Boolean).slice(0, LIMITS.websites.max),
    contacts: (p.contacts || [])
      .filter(c => c && String(c.value || '').trim())
      .map(c => ({
        type: c.type === 'phone' ? 'phone' : 'link',
        value: String(c.value).trim().slice(0, LIMITS.contactValue.max),
      }))
      .slice(0, LIMITS.contacts.max),
  };
}

// An empty profile in the shape the form expects — one blank row each so the
// user sees an input rather than only an "add" button.
export function emptyDesignerProfile(fullName = '') {
  return {
    fullName,
    styles: [],
    experienceYears: '',
    hourlyRate: '',
    currency: CURRENCY_CODES[0],
    rateUnit: RATE_UNIT_IDS[0],
    location: '',
    locationAddress: '',
    locationCoords: null,
    description: '',
    websites: [''],
    contacts: [{ type: 'link', value: '' }],
  };
}
